<template>
  <v-col cols="12">
    <v-card>
      <v-card-title class="text-h5">CEO &amp; Officer Ownership Search</v-card-title>

      <v-card-text>
        <v-row class="mt-2">
          <!-- Owner type -->
          <v-col cols="12" md="4" xl="3">
            <helpers-section-sub-header class="mb-2">Owner Type:</helpers-section-sub-header>
            <v-select
              v-model="criteria.owner_type"
              :items="ownerTypes"
              item-title="text"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>

          <!-- Ticker -->
          <v-col cols="12" md="4" xl="3">
            <helpers-section-sub-header class="mb-2">Ticker(s) (comma-separated):</helpers-section-sub-header>
            <v-text-field
              v-model="criteria.ticker"
              variant="outlined"
              density="compact"
              placeholder="e.g. AAPL, MSFT"
              hide-details
            />
          </v-col>

          <!-- Min / Max % ownership -->
          <v-col cols="12" md="4" xl="3">
            <helpers-section-sub-header class="mb-2">Ownership % Range:</helpers-section-sub-header>
            <v-row density="compact">
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.min_ownership"
                  variant="outlined"
                  type="number"
                  label="Min %"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.max_ownership"
                  variant="outlined"
                  type="number"
                  label="Max %"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Sector / Industry treeview -->
          <v-col cols="12" md="4" xl="3">
            <helpers-section-sub-header class="mb-2">Sector / Industry:</helpers-section-sub-header>
            <div v-if="selectedLabels.length" class="mb-2 d-flex flex-wrap gap-1">
              <v-chip
                v-for="label in selectedLabels"
                :key="label"
                size="small"
                closable
                @click:close="removeSelection(label)"
              >{{ label }}</v-chip>
            </div>
            <v-treeview
              v-model="selectedNodes"
              :items="sectorTree"
              item-title="name"
              open-on-click
              transition
              select-strategy="classic"
              hoverable
              return-object
              selectable
              density="compact"
            />
          </v-col>

          <v-col cols="12" class="d-flex gap-2">
            <v-btn variant="outlined" @click="search()">Search</v-btn>
            <core-export-to-excel
              url="/dashboard/form4_officer_ownership_excel"
              :criteria="params"
              :disabled="loading"
            />
          </v-col>
        </v-row>

        <v-data-table-server
          v-model:options="pagination"
          v-model:sort-by="pagination.sortBy"
          :headers="tableHeaders"
          :items="tableItems"
          :items-length="totalItems"
          :items-per-page-options="[10, 25, 50, 100]"
          :loading="loading"
          :no-data-text="loading ? 'Loading...' : 'No results found.'"
          must-sort
          class="mt-6"
        >
          <template #item="{ item }">
            <tr>
              <td>
                <a :href="'/stock/' + item.permalink">{{ item.symbol }}</a>
              </td>
              <td>{{ item.name }}</td>
              <td>{{ item.sector }}</td>
              <td>{{ item.industry }}</td>
              <td>{{ human_number(item.market_cap) }}</td>
              <td>
                <v-btn
                  variant="outlined"
                  size="small"
                  color="primary"
                  :disabled="!item.ceo_ownership_percentage"
                  @click="showTransactions(item, 'ceo')"
                >{{ formatPct(item.ceo_ownership_percentage) }}</v-btn>
              </td>
              <td>
                <v-btn
                  variant="outlined"
                  size="small"
                  color="primary"
                  :disabled="!item.c_level_officer_ownership_percentage"
                  @click="showTransactions(item, 'c_level')"
                >{{ formatPct(item.c_level_officer_ownership_percentage) }}</v-btn>
              </td>
              <td>
                <v-btn
                  variant="outlined"
                  size="small"
                  color="primary"
                  :disabled="!item.officer_ownership_percentage"
                  @click="showTransactions(item, 'officer')"
                >{{ formatPct(item.officer_ownership_percentage) }}</v-btn>
              </td>
              <td>{{ human_number(item.shares_outstanding) }}</td>
            </tr>
          </template>
        </v-data-table-server>

        <v-card-subtitle>
          Ownership % is calculated from Form 3/4/5 transactions filed in the past 2 years.
          Click any percentage to view the underlying transactions.
        </v-card-subtitle>
      </v-card-text>
    </v-card>
    <transaction-details-dialog ref="transactionDetailsDialog" />
  </v-col>
</template>

<script>
import api from '@/store/api'
import debounce from 'lodash/debounce'
import { useAppStore, useWwStore } from '@/store'
import FormattersMixin from '@/mixins/formatters'
import TransactionDetailsDialog from '@/components/page/insider-dashboard/_TransactionDetailsDialog'

export default {
  components: { TransactionDetailsDialog },
  mixins: [FormattersMixin],

  data() {
    return {
      loading: false,
      tableItems: [],
      totalItems: 0,
      selectedNodes: [],
      criteria: {
        owner_type: 'c_level',
        ticker: '',
        min_ownership: '',
        max_ownership: ''
      },
      pagination: {
        sortBy: [{ key: 'c_level_officer_ownership_percentage', order: 'desc' }],
        page: 1,
        itemsPerPage: 10
      },
      ownerTypes: [
        { text: 'C-Level Officers (CEO, CFO, COO, etc.)', value: 'c_level' },
        { text: 'CEO Only', value: 'ceo' },
        { text: 'All Officers', value: 'officer' }
      ],
      tableHeaders: [
        { title: 'Ticker', key: 'symbol' },
        { title: 'Name', key: 'name' },
        { title: 'Sector', key: 'sector' },
        { title: 'Industry', key: 'industry' },
        { title: 'Market Cap', key: 'market_cap' },
        { title: 'CEO %', key: 'ceo_ownership_percentage' },
        { title: 'C-Level %', key: 'c_level_officer_ownership_percentage' },
        { title: 'Officer %', key: 'officer_ownership_percentage' },
        { title: 'Shares Outstanding', key: 'shares_outstanding' }
      ]
    }
  },

  computed: {
    sectorTree() {
      return useWwStore().sectors
    },

    // Nodes whose parent_id is null are top-level sectors
    selectedSectors() {
      return this.selectedNodes
        .filter(n => !n.parent_id)
        .map(n => n.name)
    },

    // Nodes that have a parent_id are industries
    selectedIndustries() {
      return this.selectedNodes
        .filter(n => n.parent_id)
        .map(n => n.name)
    },

    selectedLabels() {
      return this.selectedNodes.map(n => n.name)
    },

    params() {
      return {
        ...this.criteria,
        sectors: this.selectedSectors,
        industries: this.selectedIndustries,
        rows: this.pagination.itemsPerPage,
        page: this.pagination.page,
        sort: this.pagination.sortBy[0]?.key,
        order: this.pagination.sortBy[0]?.order || 'desc'
      }
    }
  },

  watch: {
    'pagination.page'() { this.fetchData() },
    'pagination.itemsPerPage'() { this.fetchData() },
    'pagination.sortBy': { deep: true, handler() { this.fetchData() } }
  },

  methods: {
    search() {
      this.pagination.page = 1
      this.fetchData()
    },

    showTransactions(item, ownerRole) {
      this.$refs.transactionDetailsDialog.showDetails(
        { stock_id: item.stock_id, current_only: true, owner_role: ownerRole },
        {}
      )
    },

    removeSelection(label) {
      this.selectedNodes = this.selectedNodes.filter(n => n.name !== label)
    },

    formatPct(val) {
      if (val == null || val === '') return '—'
      return parseFloat(val).toFixed(2) + '%'
    },

    fetchData: debounce(function () {
      if (this.loading) return
      this.loading = true

      api
        .post('form4_officer_ownership', { params: this.params })
        .then(response => {
          this.tableItems = response.data.rows || []
          this.totalItems = response.data.records || 0
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .finally(() => {
          this.loading = false
        })
    }, 400)
  }
}
</script>
